<script setup lang="ts">
// Panel nahrávání hlasovky (SPEC.md 2.9): spodní panel přes obsah s časovačem, úrovní hlasu a textem
// ze zařízení. „Hotovo“ je jediné primární tlačítko panelu, Escape = Zrušit.
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { AlertTriangle, Loader2 } from 'lucide-vue-next'
import { Button } from '@wac-buddy/ui'
import { useVoiceStore } from '@/stores/voice'

const { t, te } = useI18n()
const voice = useVoiceStore()
const route = useRoute()
const router = useRouter()
const panel = ref<HTMLElement | null>(null)

const open = computed(() => voice.status !== 'idle')
const recording = computed(() => voice.status === 'recording')
const live = computed(() => voice.status === 'requesting' || voice.status === 'recording')

/** Čas jako 0:12. */
const timer = computed(() => {
  const total = Math.floor(voice.elapsedMs / 1000)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
})
const levelWidth = computed(() => `${Math.round(Math.min(1, Math.max(0, voice.level)) * 100)}%`)
const liveText = computed(() => [voice.finalText, voice.interimText].filter(Boolean).join(' '))
const errorText = computed(() => {
  const key = `buddy.voice.errors.${voice.errorKey}`
  return voice.errorKey && te(key) ? t(key) : t('common.error')
})

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || !open.value) return
  if (live.value) voice.cancel()
  else if (voice.status === 'error') voice.dismiss()
}

/**
 * Stránka se zavírá nebo jde do pozadí: rozdělaná nahrávka se ukončí a uloží do outboxu (nic se neztratí
 * a mikrofon se uvolní), odešle se při příštím spuštění nebo po návratu připojení.
 */
function onPageHide(): void {
  if (voice.status === 'recording') void voice.stop()
}
function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') onPageHide()
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('pagehide', onPageHide)
  document.addEventListener('visibilitychange', onVisibilityChange)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('pagehide', onPageHide)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  // Panel mizí: nic nesmí zůstat nahrávat na pozadí.
  voice.cancel()
})

// Po otevření panelu i při změně jeho obsahu se focus přesune na hlavní tlačítko (čtečka oznámí dialog).
watch(
  () => voice.status,
  async (status) => {
    if (status === 'idle') return
    await nextTick()
    panel.value?.querySelector<HTMLElement>('[data-autofocus]:not([disabled])')?.focus()
  },
)

// Hlasovka se nahrála: přejdeme do chatu, kde se objeví její bublina.
watch(
  () => voice.lastSentNoteId,
  (id) => {
    if (id && route.path !== '/buddy') void router.push('/buddy')
  },
)

/** Jednoduchý focus trap: Tab krouží uvnitř panelu. */
function onPanelKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Tab' || !panel.value) return
  const items = [...panel.value.querySelectorAll<HTMLElement>('button:not([disabled])')]
  if (!items.length) return
  const first = items[0]!
  const last = items[items.length - 1]!
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="voice-backdrop" aria-hidden="true" />
    <section
      v-if="open"
      ref="panel"
      class="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-w-content flex-col gap-4 rounded-t-xl border border-b-0 border-border-soft bg-surface px-4 pt-5 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] shadow-e3"
      role="dialog"
      aria-modal="true"
      :aria-label="t('buddy.voice.title')"
      data-testid="voice-recorder"
      @keydown="onPanelKeydown"
    >
      <!-- Žádost o mikrofon a nahrávání -->
      <template v-if="live">
        <div class="flex flex-col items-center gap-2" role="status">
          <span class="text-sm font-semibold text-text-2">{{ t('buddy.voice.recording') }}</span>
          <span
            class="font-display text-3xl font-bold tabular-nums text-text"
            :aria-label="t('buddy.voice.duration', { time: timer })"
            data-testid="voice-timer"
            >{{ timer }}</span
          >
        </div>
        <div
          class="h-2 w-full overflow-hidden rounded-pill bg-surface-2"
          role="presentation"
          data-testid="voice-level"
        >
          <div
            class="h-full rounded-pill bg-primary transition-[width] duration-100 motion-reduce:transition-none"
            :style="{ width: levelWidth }"
          />
        </div>
        <p
          v-if="liveText"
          class="m-0 max-h-24 overflow-y-auto break-words text-sm text-text-2"
          data-testid="voice-live-text"
        >
          {{ liveText }}
        </p>
        <div class="flex flex-col gap-2">
          <Button
            variant="primary"
            size="lg"
            block
            class="!min-h-fab"
            :disabled="!recording"
            data-autofocus
            data-testid="voice-stop"
            @click="voice.stop()"
          >
            {{ t('buddy.voice.done') }}
          </Button>
          <Button variant="ghost" block data-testid="voice-cancel" @click="voice.cancel()">
            {{ t('buddy.voice.cancel') }}
          </Button>
        </div>
      </template>

      <!-- Odesílání -->
      <p
        v-else-if="voice.status === 'uploading'"
        class="m-0 flex items-center justify-center gap-2 py-4 text-text-2"
        role="status"
        data-testid="voice-uploading"
      >
        <Loader2 :size="20" class="animate-spin motion-reduce:animate-none" aria-hidden="true" />
        <span>{{ t('buddy.voice.uploading') }}</span>
      </p>

      <!-- Chyba nebo zařazení do fronty -->
      <template v-else>
        <p class="m-0 flex items-start gap-2 text-text" role="alert" data-testid="voice-error">
          <AlertTriangle :size="20" class="mt-0.5 shrink-0 text-warning" aria-hidden="true" />
          <span>{{ errorText }}</span>
        </p>
        <Button
          variant="secondary"
          block
          data-autofocus
          data-testid="voice-close"
          @click="voice.dismiss()"
        >
          {{ t('buddy.voice.close') }}
        </Button>
      </template>
    </section>
  </Teleport>
</template>

<style scoped>
.voice-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
  background: var(--overlay);
}
</style>
